/**
 * 周期性星星闪烁指令
 * 用法: v-sparkle 或 v-sparkle="{ interval: 500, duration: 1200, symbol: '✦' }"
 * 在元素内随机位置周期生成星星动画
 */

import type { Directive, DirectiveBinding } from 'vue'

const SPARKLE_CLASS = 'v-sparkle'
const SPARKLE_STYLES_ID = 'v-sparkle-styles'

type SparkleOptions = {
  interval?: number
  duration?: number
  symbol?: string
}

const DEFAULT_OPTIONS: Required<SparkleOptions> = {
  interval: 500,
  duration: 1200,
  symbol: '✦',
}

const ensureSparkleStyles = (): void => {
  if (document.getElementById(SPARKLE_STYLES_ID)) return
  const style = document.createElement('style')
  style.id = SPARKLE_STYLES_ID
  style.textContent = `
    .${SPARKLE_CLASS} {
      position: absolute;
      color: #fff;
      font-size: 14px;
      pointer-events: none;
      animation: v-sparkle-anim 1.2s ease-out forwards;
    }
    @keyframes v-sparkle-anim {
      0% {
        opacity: 0;
        transform: scale(0) translateY(0);
      }
      30% {
        opacity: 1;
        transform: scale(1.2) translateY(-10px);
      }
      100% {
        opacity: 0;
        transform: scale(0.5) translateY(-30px);
      }
    }
  `
  document.head.appendChild(style)
}

const TID_KEY = '__v_sparkle_tid__'
const INTERVAL_KEY = '__v_sparkle_interval_id__'

const createOneSparkle = (el: HTMLElement, options: Required<SparkleOptions>): void => {
  const sparkle = document.createElement('span')
  sparkle.className = SPARKLE_CLASS
  sparkle.textContent = options.symbol

  const w = el.offsetWidth || 1
  const h = el.offsetHeight || 1
  const x = Math.random() * w
  const y = Math.random() * h

  sparkle.style.left = `${x}px`
  sparkle.style.top = `${y}px`
  sparkle.style.animationDuration = `${options.duration}ms`

  el.appendChild(sparkle)
  const tid = window.setTimeout(() => {
    sparkle.remove()
  }, options.duration)
  const tids = (el as HTMLElement & { [TID_KEY]?: number[] })[TID_KEY] ?? []
  tids.push(tid)
  ;(el as HTMLElement & { [TID_KEY]?: number[] })[TID_KEY] = tids
}

const parseOptions = (binding: DirectiveBinding<boolean | SparkleOptions | undefined>): Required<SparkleOptions> => {
  if (binding.value === false) return { ...DEFAULT_OPTIONS }
  if (binding.value && typeof binding.value === 'object') {
    return {
      interval: binding.value.interval ?? DEFAULT_OPTIONS.interval,
      duration: binding.value.duration ?? DEFAULT_OPTIONS.duration,
      symbol: binding.value.symbol ?? DEFAULT_OPTIONS.symbol,
    }
  }
  return { ...DEFAULT_OPTIONS }
}

export const vSparkle: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<boolean | SparkleOptions | undefined>) {
    if (binding.value === false) return
    ensureSparkleStyles()
    const position = getComputedStyle(el).position
    if (position === 'static') {
      el.style.position = 'relative'
    }
    if (getComputedStyle(el).overflow !== 'hidden') {
      el.style.overflow = 'hidden'
    }
    const options = parseOptions(binding)
    const intervalId = window.setInterval(() => {
      createOneSparkle(el, options)
    }, options.interval)
    ;(el as HTMLElement & { [INTERVAL_KEY]?: number })[INTERVAL_KEY] = intervalId
  },

  updated(el: HTMLElement, binding: DirectiveBinding<boolean | SparkleOptions | undefined>) {
    if (binding.value === false) {
      const intervalId = (el as HTMLElement & { [INTERVAL_KEY]?: number })[INTERVAL_KEY]
      if (intervalId) {
        clearInterval(intervalId)
        delete (el as HTMLElement & { [INTERVAL_KEY]?: number })[INTERVAL_KEY]
      }
      return
    }
    // 不在此处重启 interval，避免重复；若需动态开关可再扩展
  },

  unmounted(el: HTMLElement) {
    const intervalId = (el as HTMLElement & { [INTERVAL_KEY]?: number })[INTERVAL_KEY]
    if (intervalId) {
      clearInterval(intervalId)
      delete (el as HTMLElement & { [INTERVAL_KEY]?: number })[INTERVAL_KEY]
    }
    const tids = (el as HTMLElement & { [TID_KEY]?: number[] })[TID_KEY]
    if (tids) {
      tids.forEach(tid => clearTimeout(tid))
      delete (el as HTMLElement & { [TID_KEY]?: number[] })[TID_KEY]
    }
    el.style.position = ''
    el.style.overflow = ''
  },
}
